import React, { useEffect, useState } from 'react';
import { adminLogin, adminVerify, adminStats, adminListDemos, adminListNewsletter } from '@/lib/api';

export default function Admin() {
  const [authed, setAuthed] = useState(Boolean(localStorage.getItem('r360_admin_token')));
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (authed) {
      adminVerify().catch(() => {
        localStorage.removeItem('r360_admin_token');
        setAuthed(false);
      });
    }
  }, [authed]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      const { token } = await adminLogin(password);
      localStorage.setItem('r360_admin_token', token);
      setAuthed(true);
    } catch (_) {
      setErr('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  if (!authed) {
    return (
      <div className="container-r360 py-28 min-h-[80vh] flex justify-center" data-testid="admin-login">
        <form onSubmit={handleLogin} className="border border-[#1a1a1a] p-10 w-full max-w-xl">
          <div className="mono-label mb-4">// ADMIN CONSOLE</div>
          <h1 className="reveal-on-scroll font-display text-[56px] sm:text-[72px] font-bold leading-none mb-10">SIGN IN</h1>
          <label className="field-label">PASSWORD</label>
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="field-input"
            autoFocus
            data-testid="admin-password"
          />
          <button type="submit" disabled={loading} className="btn btn-primary mt-8" data-testid="admin-submit">
            {loading ? 'Checking…' : 'Authenticate →'}
          </button>
          {err && <div className="text-red-500 text-sm font-mono mt-4" data-testid="admin-error">✗ {err}</div>}
          <div className="mono-label text-[#5a5a5a] mt-8">HINT // default password is set via ADMIN_PASSWORD env var.</div>
        </form>
      </div>
    );
  }

  return <AdminDashboard onLogout={() => { localStorage.removeItem('r360_admin_token'); setAuthed(false); }} />;
}

function AdminDashboard({ onLogout }) {
  const [stats, setStats] = useState(null);
  const [demos, setDemos] = useState([]);
  const [news, setNews] = useState([]);
  const [tab, setTab] = useState('demos');

  const reload = () => {
    adminStats().then(setStats).catch(() => {});
    adminListDemos().then(setDemos).catch(() => {});
    adminListNewsletter().then(setNews).catch(() => {});
  };

  useEffect(() => { reload(); }, []);

  return (
    <div className="container-r360 py-14" data-testid="admin-dashboard">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10">
        <div>
          <div className="mono-label">// ADMIN CONSOLE</div>
          <h1 className="reveal-on-scroll font-display text-[40px] sm:text-[56px] font-bold leading-none mt-2">OPERATIONS</h1>
        </div>
        <div className="flex gap-3">
          <button onClick={reload} className="btn btn-outline" data-testid="admin-refresh">Refresh</button>
          <button onClick={onLogout} className="btn btn-outline" data-testid="admin-logout">Sign out</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-[#1a1a1a]">
        <Stat k="DEMO REQUESTS" v={stats?.demo_requests ?? '–'} />
        <Stat k="NEWSLETTER SUBS" v={stats?.newsletter_subs ?? '–'} />
        <Stat k="ACTIVE FUNNEL" v="LIVE" accent />
      </div>

      <div className="flex gap-6 mt-12 border-b border-[#1a1a1a]">
        <TabButton active={tab === 'demos'} onClick={() => setTab('demos')} testid="tab-demos">DEMO REQUESTS ({demos.length})</TabButton>
        <TabButton active={tab === 'news'} onClick={() => setTab('news')} testid="tab-news">NEWSLETTER ({news.length})</TabButton>
      </div>

      {tab === 'demos' ? (
        <div className="overflow-x-auto mt-8 border border-[#1a1a1a]" data-testid="demos-table-wrap">
          <table className="w-full text-left min-w-[900px]">
            <thead className="bg-[#0b0b0b] mono-label">
              <tr>
                <th className="p-4">CREATED</th>
                <th className="p-4">NAME</th>
                <th className="p-4">EMAIL</th>
                <th className="p-4">COMPANY</th>
                <th className="p-4">ROLE</th>
                <th className="p-4">AD SPEND</th>
                <th className="p-4">MESSAGE</th>
                <th className="p-4">SOURCE</th>
              </tr>
            </thead>
            <tbody>
              {demos.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-[#5a5a5a]">No demo requests yet.</td></tr>}
              {demos.map((d) => (
                <tr key={d.id} className="border-t border-[#1a1a1a] text-[13px]">
                  <td className="p-4 text-[#8a8a8a] font-mono">{d.created_at?.slice(0, 16).replace('T', ' ')}</td>
                  <td className="p-4">{d.full_name}</td>
                  <td className="p-4 text-[#ff5117]">{d.work_email}</td>
                  <td className="p-4">{d.company}</td>
                  <td className="p-4 text-[#c9c9c9]">{d.role || '—'}</td>
                  <td className="p-4 text-[#c9c9c9]">{d.monthly_ad_spend || '—'}</td>
                  <td className="p-4 max-w-xs truncate text-[#8a8a8a]">{d.message || '—'}</td>
                  <td className="p-4 text-[#5a5a5a] font-mono text-[11px] uppercase">{d.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="overflow-x-auto mt-8 border border-[#1a1a1a]" data-testid="news-table-wrap">
          <table className="w-full text-left min-w-[500px]">
            <thead className="bg-[#0b0b0b] mono-label">
              <tr>
                <th className="p-4">CREATED</th>
                <th className="p-4">EMAIL</th>
              </tr>
            </thead>
            <tbody>
              {news.length === 0 && <tr><td colSpan={2} className="p-8 text-center text-[#5a5a5a]">No subscribers yet.</td></tr>}
              {news.map((d) => (
                <tr key={d.id} className="border-t border-[#1a1a1a] text-[13px]">
                  <td className="p-4 text-[#8a8a8a] font-mono">{d.created_at?.slice(0, 16).replace('T', ' ')}</td>
                  <td className="p-4">{d.email}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ k, v, accent }) {
  return (
    <div className="border-r border-b border-[#1a1a1a] p-8">
      <div className="mono-label mb-3">{k}</div>
      <div className={`font-display text-[56px] font-black leading-none ${accent ? 'accent' : ''}`}>{v}</div>
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`mono-label pb-4 border-b-2 transition-colors ${active ? 'text-white border-[#ff5117]' : 'text-[#8a8a8a] border-transparent hover:text-white'}`}
    >
      {children}
    </button>
  );
}
